import { Checkbox, Form, InputNumber } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { callEventPopup, TOpenPopup2 } from "../../../../contexts/PopupContext";
import { callEventTrackingOp, state, useTrackingOp } from "../../../../contexts/TrackingOpContext";
import { formatNumber, uuid } from "../../../../util/varibles/global";
import { IActivityLog, IHarvest2, IUnit } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import Footer from "../../../Popup/_components/Footer";
import styles from "./style.module.scss";

interface IProps {
    tanks?: IHarvest2['tanks'];
    started?: {
        tank_id: string
        unit_id?: string
        group_id?: string
    }[]
    unit: IUnit
    status: number;
    current_process: number
    step_new?: number
    log?: IActivityLog
}

const TOTAL_WEIGHT_TOLERANCE = 0.001;

function StartHarvestingPopup(props: IProps) {
    const { onClose } = callEventPopup();
    const { nextState } = callEventTrackingOp();
    const [loading] = useTrackingOp(trackingState => trackingState.loading);
    const submitted = useRef(false);
    const [selected, setSelected] = useState<string[]>([]);
    const { tanks = [], status, unit, current_process } = props;

    useEffect(() => {
        if (submitted.current && !loading) {
            onClose();
        }
    }, [loading, onClose]);

    const handleSubmit = () => {
        if (selected.length === 0) {
            notify.error(NotifyCode.E51)();
            return;
        }

        submitted.current = true;
        nextState({
            group_map: selected.map(tank_id => {
                return {
                    group_id: uuid() + `/` + Date.now(),
                    tank_id,
                    unit_id: unit.unit_id,
                }
            }),
            step_state: state[status],
            current_process
        });
    }

    return <>
        <div className="w-full">
            <p className="text-[15px] text-comment">This will start the step. Step time is logged  from your action.</p>
            <div className="mt-3 font-[600] text-[15px]">Loading into tank <span className="color-danger">(*)</span></div>
            <Checkbox.Group
                className="w-full mt-2"
                value={selected}
                onChange={values => setSelected(values as string[])}
            >
                <div className="flex  flex-wrap gap-5">
                    {tanks.map(tank => (
                        <Checkbox key={tank.id} value={tank.id}>
                            {tank.name}
                        </Checkbox>
                    ))}
                </div>
            </Checkbox.Group>
        </div>
        <Footer ok={{ text: "Confirm", click: handleSubmit, loading: !!loading }} cancel={{ click: onClose }} />
    </>
}

export const startHarvesting = (props: IProps, openPopup: TOpenPopup2) => {
    return openPopup(<StartHarvestingPopup {...props} />, {
        modalProps: {
            title: "Start harvesting ?",
            closable: false
        }
    })

}

function FinishHarvestingPopup(props: IProps) {
    const { onClose } = callEventPopup();
    const { nextState } = callEventTrackingOp();
    const [loading] = useTrackingOp(trackingState => trackingState.loading);
    const [capacity] = useTrackingOp(trackingState => trackingState.op?.vessel.capacity || 0);
    const [form] = Form.useForm();
    const submitted = useRef(false);
    const { status, started = [], tanks = [], unit, current_process, step_new, log } = props;

    const currentTotalWeight = useMemo(() => (log?.real_sub_steps || []).reduce((total, item) =>
        item.real_finish_time ? total + (item.total_weight || 0) : total, 0), [log]);
    const availableFishAmount = useMemo(() => {
        const harvestedFishAmount = (log?.real_sub_steps || []).reduce((total, item) =>
            item.unit_id === unit.unit_id && item.real_finish_time
                ? total + (item.fish_amount || 0)
                : total, 0);
        const remainingUnitFishAmount = Math.max(0, unit.fish_amount - harvestedFishAmount);

        if (capacity <= 0 || unit.avg_weight <= 0) {
            return remainingUnitFishAmount;
        }

        const remainingCapacity = Math.max(0, capacity - currentTotalWeight);
        const capacityFishAmount = Math.floor(remainingCapacity / unit.avg_weight);
        return Math.min(remainingUnitFishAmount, capacityFishAmount);
    }, [capacity, currentTotalWeight, log, unit.avg_weight, unit.fish_amount, unit.unit_id]);

    useEffect(() => {
        if (submitted.current && !loading) {
            onClose();
        }
    }, [loading, onClose]);

    const getMaxFishAmount = (index: number) => {
        const groups = form.getFieldValue("groups") || [];
        const otherGroupsFishAmount = groups.reduce((total: number, group: { fish_amount?: string | number } | undefined, groupIndex: number) =>
            groupIndex === index ? total : total + Number(group?.fish_amount || 0), 0);
        return Math.max(0, availableFishAmount - otherGroupsFishAmount);
    }

    const getMaxTotalWeight = (index: number) => {
        const groups = form.getFieldValue("groups") || [];
        const otherGroupsTotalWeight = groups.reduce((total: number, group: { total_weight?: string | number } | undefined, groupIndex: number) =>
            groupIndex === index ? total : total + Number(group?.total_weight || 0), 0);
        const maxWeightByFishAmount = unit.avg_weight > 0
            ? getMaxFishAmount(index) * unit.avg_weight / 1000000 + TOTAL_WEIGHT_TOLERANCE
            : Number.POSITIVE_INFINITY;
        const maxWeightByCapacity = capacity > 0
            ? Math.max(0, (capacity - currentTotalWeight) / 1000000 - otherGroupsTotalWeight)
            : Number.POSITIVE_INFINITY;

        const maxTotalWeight = Math.min(maxWeightByFishAmount, maxWeightByCapacity);
        return Math.floor((maxTotalWeight + 1e-9) * 1000) / 1000;
    }

    const generateTotalWeight = (index: number, value: string | number | null) => {
        const totalWeightPath = ["groups", index, "total_weight"];

        if (unit.avg_weight > 0) {
            const generatedWeight = Math.min(Number(value), getMaxFishAmount(index)) * unit.avg_weight / 1000000;
            form.setFieldValue(totalWeightPath, generatedWeight > 0
                ? Number(generatedWeight.toFixed(3))
                : undefined);
        }
    }

    const generateFishAmount = (index: number, value: string | number) => {
        const fishAmountPath = ["groups", index, "fish_amount"];

        if (unit.avg_weight > 0) {
            const generatedAmount = Math.min(
                Math.floor(Number(value) * 1000000 / unit.avg_weight),
                getMaxFishAmount(index)
            );
            form.setFieldValue(fishAmountPath, generatedAmount > 0 ? generatedAmount : undefined);
        }
    }

    const normalizeFishAmount = (index: number, value: string | number) => {
        const maxFishAmount = getMaxFishAmount(index);
        if (Number(value) > maxFishAmount) {
            notify.warn(NotifyCode.W12)();
            return maxFishAmount;
        }

        return value;
    }

    const normalizeTotalWeight = (index: number, value: string | number) => {
        const maxTotalWeight = getMaxTotalWeight(index);
        if (maxTotalWeight > 0 && Number(value) > maxTotalWeight) {
            notify.warn(NotifyCode.W12)();
        }

        return Number(value) > maxTotalWeight ? maxTotalWeight : value;
    }

    const handleFishAmountChange = (index: number, rawValue: number | null) => {
        const value = Number(rawValue) || 0;
        const maxFishAmount = getMaxFishAmount(index);
        const clampedValue = Math.min(value, maxFishAmount);

        if (maxFishAmount > 0 && value > maxFishAmount) {
            notify.warn(NotifyCode.W12)();
        }

        form.setFieldValue(["groups", index, "fish_amount"], clampedValue > 0 ? clampedValue : undefined);
        generateTotalWeight(index, clampedValue);
    }

    const handleTotalWeightChange = (index: number, rawValue: number | null) => {
        const value = Number(rawValue) || 0;
        const maxTotalWeight = getMaxTotalWeight(index);
        const clampedValue = Math.min(value, maxTotalWeight);

        if (maxTotalWeight > 0 && value > maxTotalWeight) {
            notify.warn(NotifyCode.W12)();
        }

        form.setFieldValue(["groups", index, "total_weight"], clampedValue > 0 ? clampedValue : undefined);
        generateFishAmount(index, clampedValue);
    }

    const handleSubmit = (values: {
        groups?: {
            fish_amount?: string | number;
            total_weight?: string | number;
        }[]
    }) => {
        const { groups = [] } = values;
        const inputTotalWeight = groups.reduce((total, group) =>
            total + Number(group.total_weight || 0) * 1000000, 0);

        if (capacity > 0 && currentTotalWeight + inputTotalWeight > capacity) {
            notify.error(NotifyCode.E1)();
            return;
        }

        submitted.current = true;
        nextState({
            group_map: started.map((item, index) => ({
                group_id: item.group_id || "",
                tank_id: item.tank_id,
                unit_id: item.unit_id || unit.unit_id,
                fish_amount: Number(groups[index]?.fish_amount),
                total_weight: Number(groups[index]?.total_weight) * 1000000,
            })),
            step_state: state[status],
            current_process,
            step_new
        });
    }

    return <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
        <p className="text-[15px] text-comment">This will finish the step. Step time is logged  from your action.</p>
        <div className="mt-3 font-[600] text-[15px]">Input harvesting info. at tanks <span className="color-danger">(*)</span></div>

        <div className="mt-5 max-h-[55vh] overflow-y-auto pr-1">
            {started.map((item, index) => {
                const tankName = tanks.find(tank => tank.id === item.tank_id)?.name || item.tank_id;

                return <div key={item.group_id || `${item.tank_id}-${index}`} className={index > 0 ? "mt-8" : ""}>
                    <div className="mb-3 font-[600] text-[15px] uppercase">{tankName}</div>

                    <Form.Item
                        label={<span className="text-[15px]">Fish count <span className="color-danger">(*)</span></span>}
                        name={["groups", index, "fish_amount"]}
                        normalize={value => normalizeFishAmount(index, value)}
                        validateFirst
                        rules={[
                            { required: true, message: "Please input the fish count" },
                            {
                                validator: (_, value) => Number(value) > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("Fish count must be greater than 0"))
                            },
                            {
                                validator: () => {
                                    const groups = form.getFieldValue("groups") || [];
                                    const totalFishAmount = groups.reduce((total: number, group?: { fish_amount?: string | number }) =>
                                        total + Number(group?.fish_amount || 0), 0);
                                    return totalFishAmount <= availableFishAmount
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(`Total fish count cannot exceed ${formatNumber(availableFishAmount)} fish`));
                                }
                            }
                        ]}
                    >
                        <InputNumber
                            changeOnBlur={false}
                            className={`border-r-40 h-36 w-full ${styles.fishAmountInput}`}
                            formatter={(value, { input, userTyping }) => {
                                const rawValue = userTyping ? input : `${value ?? ""}`;
                                const digits = rawValue.replace(/\D/g, "");
                                if (!digits) {
                                    return "";
                                }

                                return Math.min(Number(digits), getMaxFishAmount(index)).toLocaleString("en-US");
                            }}
                            max={getMaxFishAmount(index)}
                            min={1}
                            onChange={value => handleFishAmountChange(index, value)}
                            onKeyDown={event => {
                                if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                                    return;
                                }

                                const input = event.currentTarget;
                                const hasSelection = input.selectionStart !== input.selectionEnd;
                                const currentValue = Number(form.getFieldValue(["groups", index, "fish_amount"])) || 0;

                                if (!hasSelection && currentValue >= getMaxFishAmount(index)) {
                                    notify.warn(NotifyCode.W12)();
                                    event.preventDefault();
                                }
                            }}
                            parser={value => Math.min(
                                Number(value?.replace(/\D/g, "") || 0),
                                getMaxFishAmount(index)
                            )}
                            placeholder="Input the fish count"
                            precision={0}
                            step={1}
                            suffix={<span className="text-comment">fish</span>}
                        />
                    </Form.Item>

                    <Form.Item
                        label={<span className="text-[15px]">Total weight <span className="color-danger">(*)</span></span>}
                        name={["groups", index, "total_weight"]}
                        normalize={value => normalizeTotalWeight(index, value)}
                        validateFirst
                        rules={[
                            { required: true, message: "Please input the total weight" },
                            {
                                validator: (_, value) => Number(value) > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("Total weight must be greater than 0"))
                            },
                            {
                                validator: (_, value) => Number(value) <= getMaxTotalWeight(index)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error(`Total weight cannot exceed ${formatNumber(getMaxTotalWeight(index))} tons`))
                            }
                        ]}
                    >
                        <InputNumber
                            changeOnBlur={false}
                            className={`border-r-40 h-36 w-full ${styles.fishAmountInput}`}
                            formatter={(value, { input, userTyping }) => {
                                let rawValue = (userTyping ? input : `${value ?? ""}`).replace(/,/g, "");
                                if (!rawValue) {
                                    return "";
                                }

                                if (Number(rawValue) > getMaxTotalWeight(index)) {
                                    rawValue = `${getMaxTotalWeight(index)}`;
                                }

                                const [integerPart, decimalPart] = rawValue.split(".");
                                const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");
                                return rawValue.includes(".")
                                    ? `${formattedInteger}.${decimalPart ?? ""}`
                                    : formattedInteger;
                            }}
                            max={getMaxTotalWeight(index)}
                            min={0}
                            onChange={value => handleTotalWeightChange(index, value)}
                            onKeyDown={event => {
                                if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                                    return;
                                }

                                const input = event.currentTarget;
                                const hasSelection = input.selectionStart !== input.selectionEnd;
                                const currentValue = Number(form.getFieldValue(["groups", index, "total_weight"])) || 0;
                                const cursorPosition = input.selectionStart ?? input.value.length;
                                const maxTotalWeight = getMaxTotalWeight(index);

                                if (!hasSelection && currentValue >= maxTotalWeight) {
                                    notify.warn(NotifyCode.W12)();
                                    event.preventDefault();
                                    return;
                                }

                                requestAnimationFrame(() => {
                                    const inputValue = Number(input.value.replace(/,/g, "")) || 0;
                                    if (inputValue <= maxTotalWeight) {
                                        return;
                                    }

                                    const formattedMax = maxTotalWeight.toLocaleString("en-US", {
                                        maximumFractionDigits: 3,
                                    });
                                    input.value = formattedMax;
                                    form.setFieldValue(["groups", index, "total_weight"], maxTotalWeight);
                                    generateFishAmount(index, maxTotalWeight);
                                    input.setSelectionRange(
                                        Math.min(cursorPosition, formattedMax.length),
                                        Math.min(cursorPosition, formattedMax.length)
                                    );
                                    notify.warn(NotifyCode.W12)();
                                });
                            }}
                            parser={value => Math.min(
                                Number(value?.replace(/,/g, "") || 0),
                                getMaxTotalWeight(index)
                            )}
                            placeholder="Input the total weight"
                            step={0.001}
                            suffix={<span className="text-comment">tons</span>}
                        />
                    </Form.Item>
                </div>
            })}
        </div>

        <Footer ok={{ text: "Confirm", loading: !!loading }} cancel={{ click: onClose }} />
    </Form>
}

export const finishHarvesting = (props: IProps, openPopup: TOpenPopup2) => {
    return openPopup(<FinishHarvestingPopup {...props} />, {
        modalProps: {
            title: "Finish harvesting ?",
        }
    })
}
